import tabMenuList from "@plannel/pages/TabMenuList";
import menuAccessUtil from "@plannel/pages/utils/menu-access-util";

/*
 * 현재 사용자가 접근 가능한 lv3(leaf) 화면 목록 — Sidebar 노출 규칙(lv1 sideMenu + 각 레벨 roleCheck)과 동일.
 *   ⚠️ 순회 규칙은 WorkflowPanel.js:144 의 pageOptions 와 같은 규칙의 **사본**이다(그쪽은 아이콘·그룹
 *   헤더가 얽혀 있어 이번 범위에서 통합하지 않았다). 둘 중 하나를 고치면 다른 쪽도 함께 본다.
 *
 * 용도: Agent Chat 이 "어느 화면으로 이동할 수 있는지"를 LLM 에 알려주는 카탈로그이자,
 *   이동 실행 시점의 권한 게이트(permittedKeySet). 권한 없는 화면은 애초에 제안되지 않고,
 *   제안을 우회해 들어와도 실행 단계에서 막힌다.
 */

// 메뉴명 정규화 — 공백·중점·마침표 무시, 소문자. TabMenuList.findLv3MenuByName 과 같은 규칙.
const normalizeMenuName = (v) => String(v ?? "").replace(/[\s·・.]/g, "").toLowerCase();

// userAccess: userService.getUserAccessRoles 응답. null 이면 빈 목록(= 이동 불가, 안전측).
// translate: i18n t (lv3 title 이 번역 키라 필요. skipI18n 항목만 원문).
// excludeLv1: 카탈로그에서 통째로 뺄 lv1 메뉴 코드/reduxKey — 토큰 절감이 필요할 때만 쓴다.
const buildPermittedLeafMenus = (userAccess, translate, { excludeLv1 = [] } = {}) => {
  if (!userAccess) return [];
  const t = translate ?? ((v) => v);
  const items = [];
  // lv2Label 은 lv2 그룹 직속 leaf 에만 있다(lv1 바로 밑 leaf 는 null).
  //   메뉴 검색이 결과마다 경로를 한 줄 붙이는데, lv1 만으로는 같은 이름이 갈리지 않는다 —
  //   "Settings"·"Dashboard"·"Simulation" 은 각각 여러 화면의 이름이다.
  const pushLeaf = (leaf, lv1Label, lv2Label = null) => {
    if (!leaf?.key || leaf.hide) return;
    if (!menuAccessUtil.roleCheck(userAccess, leaf.appRoles, leaf.userRoles, leaf.reduxKey)) return;
    items.push({
      key: leaf.key,
      reduxKey: leaf.reduxKey ?? null,
      name: leaf.skipI18n ? leaf.title : t(leaf.title),
      lv1Label,
      lv2Label,
    });
  };

  tabMenuList.makeSideMenuList().forEach((lv1) => {
    if (excludeLv1.includes(lv1.lv1MenuCd) || excludeLv1.includes(lv1.reduxKey)) return;
    if (!lv1.sideMenu || !menuAccessUtil.roleCheck(userAccess, lv1.appRoles, lv1.userRoles, lv1.reduxKey)) return;
    const lv1Label = t(lv1.title);
    lv1.lv2Menu?.forEach((item, idx) => {
      // subMenu[idx] = 이 항목이 lv2 그룹인지(true) lv3 leaf 직속인지(false)
      if (lv1.subMenu[idx]) {
        if (!menuAccessUtil.roleCheck(userAccess, item.appRoles, item.userRoles, item.reduxKey)) return;
        const lv2Label = t(item.menuTitle);
        item.lv3Menu?.forEach((leaf) => pushLeaf(leaf, lv1Label, lv2Label));
      } else {
        pushLeaf(item, lv1Label);
      }
    });
  });
  return items;
};

/*
 * 화면 하나의 접근 권한 판정 — 화면 안의 링크(드릴스루·우클릭 이동·대시보드 카드)를 숨길 때 쓴다.
 *
 * buildPermittedLeafMenus 와 규칙은 같지만(lv1·lv2·leaf 3단 roleCheck) **hide 를 보지 않는다**.
 *   사이드바에 안 나오고 오직 링크로만 여는 화면이 있기 때문이다(hide: true — AUDIT_TRAIL_DETAIL,
 *   RP_WORKBENCH). permittedKeySet 으로 링크를 거르면 그 둘이 정상인데도 막힌다.
 *
 * 색인은 한 번만 만든다 — 메뉴 트리는 모듈 로드 시점에 고정이고, 링크는 렌더마다 묻는다.
 */
let accessIndex = null;
const getAccessIndex = () => {
  if (accessIndex) return accessIndex;
  accessIndex = new Map();
  const put = (leaf, lv1, lv2) => {
    if (!leaf) return;
    const entry = { leaf, lv1, lv2 };
    if (leaf.key != null) accessIndex.set(leaf.key, entry);
    if (leaf.reduxKey != null) accessIndex.set(leaf.reduxKey, entry);
  };
  tabMenuList.makeSideMenuList().forEach((lv1) => {
    lv1.lv2Menu?.forEach((item, idx) => {
      // subMenu[idx] = 이 항목이 lv2 그룹인지(true) lv3 leaf 직속인지(false)
      if (lv1.subMenu[idx]) item.lv3Menu?.forEach((leaf) => put(leaf, lv1, item));
      else put(item, lv1, null);
    });
  });
  return accessIndex;
};

// keyOrReduxKey 화면을 이 사용자가 열 수 있는가. 모르는 키·userAccess 미조회는 false(안전측).
const isMenuPermitted = (userAccess, keyOrReduxKey) => {
  if (!userAccess || keyOrReduxKey == null) return false;
  const entry = getAccessIndex().get(keyOrReduxKey);
  if (!entry) return false;
  // 미출시(unreleased)는 hide 와 달리 링크로도 열지 않는다 — TabMenuList.isUnreleasedMenu 주석
  if (entry.lv1?.unreleased || entry.leaf?.unreleased) return false;
  const pass = (m) => !m || menuAccessUtil.roleCheck(userAccess, m.appRoles, m.userRoles, m.reduxKey);
  return pass(entry.lv1) && pass(entry.lv2) && pass(entry.leaf);
};

// 이동 허용 여부 판정용 key 집합. key/reduxKey 둘 다 담아 어느 쪽으로 지목해도 통과시킨다.
const permittedKeySet = (items) => {
  const set = new Set();
  (items ?? []).forEach((m) => {
    if (m?.key != null) set.add(m.key);
    if (m?.reduxKey != null) set.add(m.reduxKey);
  });
  return set;
};

// 카탈로그 안에서 이름으로 찾기 — 정확 일치 우선 → 부분 일치. 권한 밖 화면은 애초에 목록에 없다.
const findByName = (items, text) => {
  const target = normalizeMenuName(text);
  if (!target) return null;
  let partial = null;
  for (const m of items ?? []) {
    const name = normalizeMenuName(m?.name);
    if (!name) continue;
    if (name === target) return m;
    if (!partial && name.length >= 2 && (name.includes(target) || target.includes(name))) partial = m;
  }
  return partial;
};

// 부분 일치 후보 상위 N건 — LLM 이 지목한 이름을 해석하지 못했을 때 되묻기용.
const searchByName = (items, text, limit = 3) => {
  const target = normalizeMenuName(text);
  if (!target) return [];
  return (items ?? [])
    .filter((m) => {
      const name = normalizeMenuName(m?.name);
      return name.length >= 2 && (name.includes(target) || target.includes(name));
    })
    .slice(0, limit);
};

/*
 * 메뉴 검색(components/MenuSearch)용 순위 매김 검색.
 *
 * searchByName 과 나누어 둔 이유: 그쪽은 **LLM 이 지목한 이름을 못 알아들었을 때 되묻는 후보**라
 *   `target.includes(name)`(= 사용자가 더 길게 말한 경우)까지 받아 주고 상위 3건이면 족하다.
 *   사람이 한 글자씩 치는 검색에 그 규칙을 그대로 쓰면 두 글자만 쳐도 온 메뉴가 걸린다.
 *
 * 순위: 완전 일치 → 앞자리 일치 → 이름 포함 → 경로(lv1·lv2) 포함.
 *   경로 일치를 맨 뒤에 따로 두는 게 핵심이다 — "dp" 처럼 모듈명을 치면 그 모듈 화면이
 *   전부 나오는데, 이름이 직접 맞은 화면보다 먼저 서면 안 된다.
 * 각 묶음 안에서는 카탈로그 순서(= 사이드바 순서)를 지킨다. 같은 검색어에 같은 줄이 서야 한다.
 */
const rankByName = (items, text, limit = 50) => {
  const target = normalizeMenuName(text);
  if (!target) return (items ?? []).slice(0, limit);

  const exact = [], prefix = [], contains = [], byPath = [];
  for (const m of items ?? []) {
    const name = normalizeMenuName(m?.name);
    if (name === target) exact.push(m);
    else if (name.startsWith(target)) prefix.push(m);
    else if (name.includes(target)) contains.push(m);
    else if (normalizeMenuName(m?.lv1Label).includes(target)
          || normalizeMenuName(m?.lv2Label).includes(target)) byPath.push(m);
  }
  return exact.concat(prefix, contains, byPath).slice(0, limit);
};

/*
 * 표시 문자열 안에서 검색어가 맞은 구간 [start, end). 못 맞으면 null.
 *
 * ⚠️ 정규화된 문자열에서 찾은 위치를 **원문 위치로 되돌려야** 한다. normalizeMenuName 이
 *   공백·중점·마침표를 지우므로 두 문자열의 인덱스가 어긋나 있다. 그대로 쓰면 "BF Settings"
 *   에서 "bfs" 를 찾았을 때 엉뚱한 칸이 칠해진다(공백 한 칸만큼 밀린다).
 */
const matchRange = (text, query) => {
  const target = normalizeMenuName(query);
  if (!target) return null;
  const kept = [], origin = [];
  for (let i = 0; i < String(text ?? "").length; i += 1) {
    const c = normalizeMenuName(text[i]);
    if (c) { kept.push(c); origin.push(i); }
  }
  const at = kept.join("").indexOf(target);
  if (at < 0) return null;
  const last = origin[at + target.length - 1];
  if (origin[at] === undefined || last === undefined) return null;
  return [origin[at], last + 1];
};

const menuCatalogUtil = {
  buildPermittedLeafMenus, permittedKeySet, isMenuPermitted, findByName, searchByName,
  normalizeMenuName, rankByName, matchRange,
};
export default menuCatalogUtil;
